import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TipCalculator = ({ 
  subtotal = 0,
  participants = [],
  currentUserId = null,
  onTipChange = () => {},
  currentTip = { percentage: 10, amount: 0, perPerson: 0 }
}) => {
  const [tipMode, setTipMode] = useState('percentage'); // 'percentage' | 'amount'
  const [customPercentage, setCustomPercentage] = useState(currentTip?.percentage);
  const [customAmount, setCustomAmount] = useState(currentTip?.amount);

  const presetPercentages = [0, 5, 10, 15, 20];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const getDisplayName = (participant) => {
    return participant?.id === currentUserId ? 'Tú' : participant?.name;
  };

  const calculateTip = (percentage, amount) => {
    let tipAmount = 0;
    
    if (tipMode === 'percentage') {
      tipAmount = (subtotal * percentage) / 100;
    } else {
      tipAmount = amount;
    }

    const perPerson = participants?.length > 0 ? tipAmount / participants?.length : 0;
    
    return {
      percentage: tipMode === 'percentage' ? percentage : (amount / subtotal) * 100,
      amount: tipAmount,
      perPerson: perPerson
    };
  };

  const handlePresetPercentage = (percentage) => {
    setCustomPercentage(percentage);
    const tipData = calculateTip(percentage, 0);
    onTipChange(tipData);
  };

  const handleCustomPercentageChange = (e) => {
    const percentage = parseFloat(e?.target?.value) || 0;
    setCustomPercentage(percentage);
    const tipData = calculateTip(percentage, 0);
    onTipChange(tipData);
  };

  const handleCustomAmountChange = (e) => {
    const amount = parseFloat(e?.target?.value) || 0;
    setCustomAmount(amount);
    const tipData = calculateTip(0, amount);
    onTipChange(tipData);
  };

  const toggleTipMode = () => {
    const newMode = tipMode === 'percentage' ? 'amount' : 'percentage';
    setTipMode(newMode);
    
    if (newMode === 'percentage') {
      const tipData = calculateTip(customPercentage, 0);
      onTipChange(tipData);
    } else {
      const tipData = calculateTip(0, customAmount);
      onTipChange(tipData);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Calculadora de Propina</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleTipMode}
          iconName="RefreshCw"
          iconPosition="left"
          iconSize={14}
        >
          {tipMode === 'percentage' ? 'Cantidad fija' : 'Porcentaje'}
        </Button>
      </div>
      {/* Bill Summary */}
      <div className="mb-4 p-3 bg-muted/30 rounded-lg">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-text-secondary">Subtotal de la cuenta:</span>
          <span className="font-mono text-foreground">{formatAmount(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Número de personas:</span>
          <span className="font-mono text-foreground">{participants?.length}</span>
        </div>
      </div>
      {tipMode === 'percentage' ? (
        <>
          {/* Preset Percentages */}
          <div className="mb-4">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Porcentajes sugeridos:
            </label>
            <div className="grid grid-cols-5 gap-2">
              {presetPercentages?.map((percentage) => (
                <Button
                  key={percentage}
                  variant={customPercentage === percentage ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePresetPercentage(percentage)}
                  className="text-xs"
                >
                  {percentage}%
                </Button>
              ))}
            </div>
          </div>

          {/* Custom Percentage */}
          <div className="mb-4">
            <Input
              label="Porcentaje personalizado"
              type="number"
              value={customPercentage}
              onChange={handleCustomPercentageChange}
              placeholder="Ingresa porcentaje"
              min="0"
              max="100"
              step="0.5"
            />
          </div>
        </>
      ) : (
        <div className="mb-4">
          <Input
            label="Cantidad de propina"
            type="number"
            value={customAmount}
            onChange={handleCustomAmountChange}
            placeholder="Ingresa cantidad en €"
            min="0"
            step="0.01"
          />
        </div>
      )}
      {/* Tip Breakdown */}
      <div className="space-y-3 p-3 bg-success/5 border border-success/20 rounded-lg">
        <div className="flex justify-between">
          <span className="text-sm font-medium text-foreground">Propina total:</span>
          <span className="font-mono font-semibold text-foreground">
            {formatAmount(currentTip?.amount)}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-sm text-text-secondary">Por persona:</span>
          <span className="font-mono text-foreground">
            {formatAmount(currentTip?.perPerson)}
          </span>
        </div>

        <div className="flex justify-between pt-2 border-t border-success/20">
          <span className="text-sm font-medium text-foreground">Total con propina:</span>
          <span className="font-mono font-semibold text-lg text-foreground">
            {formatAmount(subtotal + currentTip?.amount)}
          </span>
        </div>
      </div>
      {/* Distribution Preview */}
      {participants?.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-foreground mb-2">
            Distribución por persona:
          </h4>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {participants?.map((participant) => (
              <div key={participant?.id} className="flex justify-between text-sm">
                <span className="text-text-secondary">{getDisplayName(participant)}:</span>
                <span className="font-mono text-foreground">
                  +{formatAmount(currentTip?.perPerson)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TipCalculator;